import 'keen-slider/keen-slider.min.css'

import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { Arrow } from 'app/components/Common/Arrow'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles } from 'app/theme'
import { Media } from 'app/theme/media'
import { easeOutQuint, positionStyle } from 'app/utils/customFunctions'
import { useVocabularyData } from 'app/utils/vocabulary'
import { useKeenSlider } from 'keen-slider/react'
import React, { memo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  images?: ImageProps[]
  languageCode: string
}

export const Gallery = memo(function Gallery({ images, languageCode }: Props) {
  if (!images) {
    return null
  }

  if (images.length < 1) {
    return null
  }

  const [ref, isRefVisible] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  })

  const [currentSlide, setCurrentSlide] = useState(0)
  const containerRef = useRef<any>()
  const [details, setDetails] = useState<any>(null)

  const [sliderImage, instanceRefMain] = useKeenSlider({
    loop: true,
    drag: true,
    defaultAnimation: {
      duration: 1800,
      easing: easeOutQuint,
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel)
    },
    detailsChanged(s) {
      images.length > 1 ? setDetails(s.track.details) : null
    },
    breakpoints: {
      '(max-width: 1199px)': {
        slides: {
          origin: 'center',
          perView: 1.2,
          spacing: 16,
        },
      },
    },
  })

  return (
    <Container ref={ref}>
      <Label text={useVocabularyData('gallery', languageCode)} />
      <Counter>{`${currentSlide + 1}/${images.length}`}</Counter>
      {images ? (
        <Wrapper
          className={isRefVisible ? 'visible' : undefined}
          ref={containerRef}
        >
          <Slider
            className={images.length > 1 ? 'grab-cursor' : undefined}
            ref={sliderImage}
          >
            {images.map((item, index) => (
              <Slide key={index} className="keen-slider__slide">
                <Inner style={positionStyle(instanceRefMain, details, index)}>
                  <Image {...item} />
                </Inner>
              </Slide>
            ))}
          </Slider>

          <Media greaterThanOrEqual="desktopSmall">
            {images.length > 1 ? (
              <Arrows>
                <Arrow
                  onClick={(e) => {
                    instanceRefMain.current?.prev()
                    e.stopPropagation()
                  }}
                  variant="default"
                />

                <Arrow
                  direction="R"
                  onClick={(e) => {
                    instanceRefMain.current?.next()
                    e.stopPropagation()
                  }}
                  variant="default"
                />
              </Arrows>
            ) : null}
          </Media>
        </Wrapper>
      ) : null}
    </Container>
  )
})

const Container = styled.section`
  padding: 7.5rem 5.833vw 2.5rem;
  text-align: center;

  @media (max-width: 1199px) {
    margin: 5.25rem auto 1.5rem;
    padding: 0;
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label}

  @media (max-width: 1199px) {
    margin-bottom: 0;
  }
`

const Counter = styled.div`
  ${styles.label}

  margin-top: 0.75rem;
  font-size: 1rem;
`

const Wrapper = styled.div`
  opacity: 0;
  margin-top: 2.5rem;
  position: relative;
  transform: translateX(-3.75rem);
  &.visible {
    opacity: 1;
    transform: none;
    transition: 1s;
  }

  @media (max-width: 1199px) {
    margin-top: 2.25rem;
  }
`

const Slider = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;
  position: relative;
`

const Slide = styled.div`
  aspect-ratio: 1.8;
  max-height: 43.75rem;
  overflow: hidden;
  position: relative;

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    aspect-ratio: 1.4;
  }
`

const Inner = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transition-timing-function: cubic-bezier(0.85, 0, 0.15, 1);

  @media (max-width: 1199px) {
    transform: none !important;
  }
`

const Arrows = styled.div`
  > div {
    position: absolute;
    bottom: 3rem;
    z-index: 3;
    &:first-of-type {
      left: calc(50% - 4.25rem);
    }
    &:last-of-type {
      right: calc(50% - 4.25rem);
    }
  }
`
